
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiShoppingCart, FiHeart, FiEye } from 'react-icons/fi';
import { useCurrency } from '../context/CurrencyContext';

export default function ProductCard({ product }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Get all available images for the product
  // Handle both old WooCommerce API format (images array) and new optimized API format (image + gallery)
  const productImages = [];

  if (product.images && Array.isArray(product.images)) {
    // Old WooCommerce API format
    productImages.push(...product.images);
  } else {
    // New optimized API format
    if (product.image) {
      productImages.push({ src: product.image });
    }
    if (product.gallery && Array.isArray(product.gallery)) {
      product.gallery.forEach(galleryImage => {
        if (galleryImage && galleryImage !== product.image) {
          productImages.push({ src: galleryImage });
        }
      });
    }
  }

  const hasMultipleImages = productImages.length > 1;


  // Switch to second image immediately on hover
  useEffect(() => {
    if (isHovered && hasMultipleImages) {
      setCurrentImageIndex(1); // Show second image immediately
    } else {
      setCurrentImageIndex(0); // Return to first image immediately
    }
  }, [isHovered, hasMultipleImages]);

  const currentImage = productImages[currentImageIndex] || productImages[0] || { src: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==' };

  const { formatPrice } = useCurrency();

  const isOnSale = product.sale_price && product.sale_price < product.regular_price;
  const discountPercentage = isOnSale
    ? Math.round(((product.regular_price - product.sale_price) / product.regular_price) * 100)
    : 0;

  return (
    <Link
      to={product.slug ? `/products/${product.slug}` : `/product/${product.id}`}
      className="block bg-black rounded-xl overflow-hidden group relative shadow-lg border border-gray-800 hover:shadow-pink-500/20 hover:border-pink-500/30 transition-all duration-500 flex flex-col cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative flex-1 min-h-0" style={{ height: '250px' }}>
        <img
          src={currentImage.src}
          alt={product.name}
          className="w-full h-full object-cover object-top transition-all duration-500 ease-in-out group-hover:scale-105"
          loading="lazy"
          decoding="async"
          onLoad={(e) => {
            e.target.style.opacity = '1';
          }}
          onError={(e) => {
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
          }}
          style={{ opacity: 0, transition: 'opacity 0.3s ease-in-out' }}
        />

        {/* Sale Badge */}
        {isOnSale && (
          <div className="absolute top-2 left-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-2 py-1 rounded-lg text-xs font-bold shadow-lg transform group-hover:scale-105 transition-transform duration-300">
            -{discountPercentage}%
          </div>
        )}

        {/* Stock Status */}
        {product.stock_status === 'outofstock' && (
          <div className="absolute top-2 right-2 bg-gray-800/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs font-semibold">
            Out of Stock
          </div>
        )}
      </div>

      {/* Product Info Section - Always visible with fixed height */}
      <div className="p-3 bg-black flex-shrink-0" style={{ minHeight: '80px' }}>
        <h3 className="text-white text-sm font-semibold mb-2 line-clamp-2 leading-tight transition-colors group-hover:text-pink-500">
          {product.name}
        </h3>

        {/* Price */}
        <div className="flex items-center space-x-2">
          <span className="text-red-500 font-bold text-base">
            {formatPrice(product.sale_price || product.price)}
          </span>
          {isOnSale && (
            <span className="text-white line-through text-sm">
              {formatPrice(product.regular_price)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
